/**
 * Analytics Service for MockupPro
 * Phase 5: Growth tracking and conversion optimization
 * Phase 6: Engagement loop tracking
 * 
 * Tracks user behavior, engagement loops, and conversion events
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Event types that can be tracked
export const TRACK_EVENTS = {
  // Core actions
  IMAGE_GENERATED: 'image_generated',
  MAKE_IT_BETTER_CLICKED: 'make_it_better_clicked',
  ENHANCE_USED: 'enhance_used',
  UPSCALE_USED: 'upscale_used',
  VARIATIONS_GENERATED: 'variations_generated',
  
  // Engagement
  PAGE_VIEW: 'page_view',
  SESSION_START: 'session_start',
  
  // Drop-off points
  DROPPED_AFTER_GENERATION: 'dropped_after_generation',
  CREDITS_EXHAUSTED_EXIT: 'credits_exhausted_exit',
  
  // Monetization
  LOW_CREDIT_WARNING_SHOWN: 'low_credit_warning_shown',
  UPGRADE_MODAL_SHOWN: 'upgrade_modal_shown',
  UPGRADE_MODAL_CLICKED: 'upgrade_modal_clicked',
  UPGRADE_ATTEMPTED: 'upgrade_attempted',
  UPGRADE_COMPLETED: 'upgrade_completed',
  
  // Nudges
  NUDGE_MAKE_IT_BETTER_SHOWN: 'nudge_make_it_better_shown',
  NUDGE_ENHANCE_SHOWN: 'nudge_enhance_shown',
  NUDGE_UPSCALE_SHOWN: 'nudge_upscale_shown',
  NUDGE_CLICKED: 'nudge_clicked',
  SMART_NUDGE_SHOWN: 'smart_nudge_shown',
  SMART_NUDGE_DISMISSED: 'smart_nudge_dismissed',
  
  // Engagement Loop Tracking
  LOOP_STEP_COMPLETED: 'loop_step_completed',
  LOOP_COMPLETED: 'loop_completed',
  LOOP_DROPPED: 'loop_dropped',
  
  // UI interactions
  BUTTON_CLICK: 'button_click',
  MODAL_OPEN: 'modal_open',
  MODAL_CLOSE: 'modal_close',
  FEATURE_USED: 'feature_used',
};

// Engagement loop steps
export const LOOP_STEPS = {
  GENERATE: 'generate',
  IMPROVE: 'improve',
  ENHANCE: 'enhance',
  UPSCALE: 'upscale',
};

/**
 * Track an event to the analytics backend
 * @param {string} eventType - The type of event to track
 * @param {object} metadata - Additional data about the event
 * @param {string} token - Auth token (optional, will skip if not provided)
 */
export const trackEvent = async (eventType, metadata = {}, token = null) => {
  // Skip if no token (user not logged in)
  if (!token) {
    const storedToken = localStorage.getItem('token');
    if (!storedToken) return;
    token = storedToken;
  }
  
  try {
    const response = await fetch(
      `${API_URL}/api/analytics/track?event_type=${encodeURIComponent(eventType)}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(metadata),
      }
    );
    
    if (!response.ok) {
      console.warn('[Analytics] Failed to track event:', eventType);
    }
  } catch (error) {
    // Silently fail - analytics should not break the app
    console.warn('[Analytics] Error tracking event:', error.message);
  }
};

/**
 * Track low credit warning display
 * @param {number} credits - Current credit balance
 * @param {string} trigger - What triggered the warning (warning/critical)
 */
export const trackLowCreditWarning = (credits, trigger, token) => {
  trackEvent(TRACK_EVENTS.LOW_CREDIT_WARNING_SHOWN, { credits, trigger }, token);
};

/**
 * Track upgrade modal display
 * @param {string} trigger - What triggered the modal
 */
export const trackUpgradeModalShown = (trigger, token) => {
  trackEvent(TRACK_EVENTS.UPGRADE_MODAL_SHOWN, { trigger }, token);
};

/**
 * Track upgrade modal click (user clicked upgrade button)
 * @param {string} plan - The plan they clicked to upgrade to
 */
export const trackUpgradeModalClicked = (plan, token) => {
  trackEvent(TRACK_EVENTS.UPGRADE_MODAL_CLICKED, { plan }, token);
};

/**
 * Track nudge display
 * @param {string} nudgeType - The type of nudge shown
 */
export const trackNudgeShown = (nudgeType, token) => {
  const eventMap = {
    'make_it_better': TRACK_EVENTS.NUDGE_MAKE_IT_BETTER_SHOWN,
    'enhance': TRACK_EVENTS.NUDGE_ENHANCE_SHOWN,
    'upscale': TRACK_EVENTS.NUDGE_UPSCALE_SHOWN,
  };
  trackEvent(eventMap[nudgeType] || TRACK_EVENTS.NUDGE_MAKE_IT_BETTER_SHOWN, {}, token);
};

/**
 * Track nudge click
 * @param {string} nudgeType - The type of nudge clicked
 */
export const trackNudgeClicked = (nudgeType, token) => {
  trackEvent(TRACK_EVENTS.NUDGE_CLICKED, { nudge_type: nudgeType }, token);
};

/**
 * Track button clicks for conversion analysis
 * @param {string} buttonName - Name of the button
 * @param {string} page - Current page
 */
export const trackButtonClick = (buttonName, page, token) => {
  trackEvent(TRACK_EVENTS.BUTTON_CLICK, { button: buttonName, page }, token);
};

/**
 * Track smart nudge display (delayed nudge after generation)
 * @param {string} nudgeType - Type of nudge shown
 * @param {number} delaySeconds - Seconds before nudge appeared
 */
export const trackSmartNudgeShown = (nudgeType, delaySeconds, token) => {
  trackEvent(TRACK_EVENTS.SMART_NUDGE_SHOWN, { nudge_type: nudgeType, delay_seconds: delaySeconds }, token);
};

/**
 * Track smart nudge dismissed
 */
export const trackSmartNudgeDismissed = (token) => {
  trackEvent(TRACK_EVENTS.SMART_NUDGE_DISMISSED, {}, token);
};

/**
 * Track engagement loop step completion
 * @param {string} step - The completed step (generate, improve, enhance, upscale)
 * @param {object} loopState - Current state of the loop
 */
export const trackLoopStep = (step, loopState, token) => {
  trackEvent(TRACK_EVENTS.LOOP_STEP_COMPLETED, { step, ...loopState }, token);
};

/**
 * Track full loop completion
 * @param {object} loopState - Final state of the loop
 */
export const trackLoopCompleted = (loopState, token) => {
  trackEvent(TRACK_EVENTS.LOOP_COMPLETED, loopState, token);
};

/**
 * Track loop drop-off
 * @param {string} lastStep - Last completed step before drop-off
 * @param {string} reason - Why user dropped (low_credits, inactivity, etc.)
 */
export const trackLoopDropped = (lastStep, reason, token) => {
  trackEvent(TRACK_EVENTS.LOOP_DROPPED, { last_step: lastStep, reason }, token);
};

export default {
  trackEvent,
  trackLowCreditWarning,
  trackUpgradeModalShown,
  trackUpgradeModalClicked,
  trackNudgeShown,
  trackNudgeClicked,
  trackButtonClick,
  trackSmartNudgeShown,
  trackSmartNudgeDismissed,
  trackLoopStep,
  trackLoopCompleted,
  trackLoopDropped,
  TRACK_EVENTS,
  LOOP_STEPS,
};
